<script setup lang="ts">
import { ref, onMounted, computed, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Icon } from '@iconify/vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { formatedDate } from '@/constants'
import { type Batch } from '@/types/batch-types'
import { getBatchById, sendToSupplier, editBatch } from '@/api/batches'
import BatchActions from '@/components/features/batches/components/BatchActions.vue'
import EditGreigForm from '@/components/features/batches/forms/EditGreigForm.vue'
import EditOffsiteForm from '@/components/features/batches/forms/EditOffsiteForm.vue'
import { useBatchForm } from '@/composables/useBatchForm'
import type { Supplier } from '@/types/supplier-types'
import { getSupplierList } from '@/api/suppliers'
import SendToSupplierForm from '@/components/features/batches/forms/SendToSupplierForm.vue'
import { useSendToSupplierForm } from '@/composables/useSendToSupplierForm'
import GeneratePackingListDialog from '@/components/features/batches/components/GeneratePackingListDialog.vue'
import type { ErrorResponse } from '@/types/error-types'
import {RollState} from "@/types/roll-types";

const route = useRoute()
const router = useRouter()
const batchId = route.params.id as string
const { showToast } = useBatchForm('add')

const batch: Ref<any | null> = ref(null)
const loading = ref(true)
const error = ref<string | null>(null)
const showSendToSupplierDialog = ref(false)
const showGeneratePackingListDialog = ref(false)
const rolls = ref([])
const isSentToSupplier = ref(false)
const selectedRolls = ref<any>([])
const suppliers = ref<Supplier[] | undefined>()

const { values, handleSubmit, setErrors } = useSendToSupplierForm()

const getSupplierName = (supplierId: string | undefined) => {
  return suppliers.value?.find((supplier) => supplier.id == supplierId)?.name
}

const fetchBatch = async () => {
  loading.value = true
  error.value = null
  try {
    const response: Batch = await getBatchById(batchId)
    batch.value = {
      ...response,
      quality: { name: response.knittingQualityId },
      dateKnitted: response.dateKnitted ? new Date(response.dateKnitted) : null,
      finishingSupplierId: response.finishingSupplierId
    }
    rolls.value = batch.value.rolls
  } catch (err) {
    error.value = 'Failed to fetch batch data. Please try again.'
  } finally {
    loading.value = false
  }
}

const availableRollsForSupplier = computed(() => {
  if (!batch.value?.rolls) return []

  return batch.value.rolls.filter((roll: any) =>
      roll.rollState === RollState.Greig
  )
})

const formDisabled = ref(true)
const toggleEditMode = () => (formDisabled.value = !formDisabled.value)

const handleSendToSupplier = handleSubmit(async () => {
  const formData = {
    batchId: batchId,
    supplierId: values.supplierId,
    supplierName: getSupplierName(values.supplierId) as string,
    supplierAddressId: values.supplierAddressId,
    purchaseOrderNumber: values.purchaseOrderNumber,
    packingListReference: values.packingListReference,
    qualityId: values.qualityId,
    rollStatus: values.rollStatus,
    rolls: selectedRolls.value.map((rollId: string) => ({ rollId })),
    notes: values.notes
  }
  loading.value = true
  await sendToSupplier(formData)
    .then(() => {
      loading.value = false
      showToast(
        'success',
        'Batch Sent to Supplier!',
        'You have successfully sent a batch to a supplier.'
      )
      isSentToSupplier.value = true
      showSendToSupplierDialog.value = false
      fetchBatch()
    })
    .catch((error: {response?: {data: ErrorResponse}}) => {
      loading.value = false
      const errorMessages = error.response?.data?.errors?.map((err) => err.description).join('\n') || 'Unable to send batch to supplier.'
      showToast('error', 'An error occurred!', errorMessages)
    })
})

const closeClosedPackingListDialog = () => {
  showGeneratePackingListDialog.value = false
}

const handleReturnFromSupplier = async () => {
  loading.value = true
  router.push(`/batches/${batchId}/returned-from-supplier`)
}

const saveBatch = async () => {
  if (batch.value) {
    const formattedDate = batch.value.dateKnitted
      ? new Date(batch.value.dateKnitted).toISOString() // Keep only "YYYY-MM-DD"
      : null

    const payload = {
      ...batch.value,
      supplierId: batch.value.supplierId?.id ?? null,
      dateKnitted: formattedDate,
      costPerKgAmount: batch.value.costPerKgAmount > 0 ? batch.value.costPerKgAmount : 1,
      costPerMetreAmount: batch.value.costPerMetreAmount > 0 ? batch.value.costPerMetreAmount : 1
    }

    console.log('Payload sent to backend:', payload)

    await editBatch(payload, batchId)
      .then(() => {
        showToast('info', 'Batch Updated!', 'You have successfully updated a batch.')
      })
      .catch((err) => {
        console.error('Error response:', err.response?.data || err.message)
        showToast('error', 'An error occurred!', 'Unable to update a batch.')
      })
  }
}

const saveAndToggleEditMode = () => {
  saveBatch()
  toggleEditMode()
}

const formattedState = computed(() => {
  if (!batch.value) {
    return 'Unknown State'
  }

  return batch.value.state
})

onMounted(async () => {
  await fetchBatch()
  suppliers.value = await getSupplierList()
})
</script>

<template>
  <div v-if="loading">Loading batch data...</div>

  <div v-else-if="error" class="text-red-500">{{ error }}</div>

  <div v-else class="p-10 bg-white rounded-xl">
    <div class="flex justify-between items-center mb-3">
      <RouterLink to="/batches" class="flex items-center gap-x-2 group hover:text-primary-default">
        <Icon
          icon="material-symbols:arrow-back-rounded"
          class="text-3xl group-hover:translate-x-2 transition-transform"
        />
        <span>Back</span>
      </RouterLink>

      <h1 class="font-semibold text-2xl capitalize">
        {{
          formattedState === 0
            ? 'Greig Batch'
            : formattedState === 1
              ? 'Offsite Batch'
              : 'Finished Batch'
        }}
      </h1>

      <div class="flex flex-col gap-2 items-end">
        <div v-if="batch.state == 0" class="flex items-center gap-2">
          <Button
            @click="
              () => {
                showSendToSupplierDialog = true
              }
            "
            label="Send to supplier"
            class="min-w-48"
          >
            <template #icon>
              <Icon icon="mdi:send" class="text-xl ml-3 order-1" />
            </template>
          </Button>
          <Button
              v-if="formDisabled"
              outlined
              @click="toggleEditMode"
              label="Edit this Batch"
              class="min-w-48"
          >
            <template #icon>
              <Icon icon="basil:edit-solid" class="text-xl ml-3 order-1" />
            </template>
          </Button>
          <Button
              v-else
              outlined
              @click="saveAndToggleEditMode"
              label="Save this Batch"
              class="min-w-48"
          >
            <template #icon>
              <Icon icon="basil:save-solid" class="text-xl ml-3 order-1" />
            </template>
          </Button>
        </div>

        <div v-if="batch.state == 1" class="flex items-center gap-2">
          <Button
            outlined
            @click="
              () => {
                showGeneratePackingListDialog = true
              }
            "
            label="Generate Packing List"
            class="min-w-48"
          >
            <template #icon>
              <Icon icon="mdi:note-add" class="text-xl ml-3 order-1" />
            </template>
          </Button>
          <Button @click="handleReturnFromSupplier" label="Return from supplier" class="min-w-48">
            <template #icon>
              <Icon icon="fluent:person-add-28-filled" class="text-xl ml-3 order-1" />
            </template>
          </Button>
        </div>
        <div v-if="batch.state == 2" class="flex items-center gap-2">
          <Button @click="() => {}" label="View Report" class="min-w-48">
            <template #icon>
              <Icon icon="mdi:file-document" class="text-xl ml-3 order-1" />
            </template>
          </Button>
        </div>
      </div>
    </div>

    <BatchActions :batch="batch"  />

    <EditGreigForm v-if="batch?.state === 0" v-model="batch" :disabled="formDisabled" />

    <EditOffsiteForm v-if="batch?.state === 1" v-model="batch" :disabled="formDisabled" />
  </div>

  <!-- Send to supplier dialog -->
  <Dialog
    :visible="showSendToSupplierDialog"
    :draggable="false"
    :style="{ width: '70rem' }"
    :closable="false"
  >
    <h3 class="font-bold text-md">Send to supplier</h3>
    <SendToSupplierForm
      :values="values"
      :rolls="availableRollsForSupplier"
      :onSubmit="handleSendToSupplier"
      :setErrors="setErrors"
      v-model="selectedRolls"
    />

    <div class="flex flex-row gap-8 justify-end items-center mt-8">
      <Button
        severity="secondary"
        @click="
          () => {
            showSendToSupplierDialog = false
          }
        "
        label="Cancel"
        class="min-w-48"
      >
        <template #icon>
          <Icon icon="mdi:close" class="text-xl ml-3 order-1" />
        </template>
      </Button>
      <Button @click="handleSendToSupplier" label="Send to Supplier" class="min-w-48">
        <template #icon>
          <Icon icon="mdi:send" class="text-xl ml-3 order-1" />
        </template>
      </Button>
    </div>
  </Dialog>

  <GeneratePackingListDialog
    :showGeneratePackingListDialog="showGeneratePackingListDialog"
    :batchNumber="batch?.batchNumber"
    :batchId="batchId"
    :finishingSupplierId="batch?.finishingSupplierId"
    :rolls="batch?.rolls"
    :close="closeClosedPackingListDialog"
  />
</template>